import { pageIntroContent } from '@insify/css/classes.module.css';
import { HighCare, RichContentWithTooltip, useWizard } from '@insify/funnel-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useParamsQuery } from '../../hooks/useQueries';
import { stepsContext } from '../../hooks/useSteps';

import type { Commands } from '../../models/commands';
import type { Params } from '../../models/queries';
import type { HighCareFormType } from '@insify/funnel-components';
import type { SubmitHandler } from 'react-hook-form';

export const HighCarePage = (): JSX.Element | null => {
  const { t } = useTranslation(['common', 'funnel', 'validation']);
  const { showPreviousPage, showNextPage } = useWizard();
  const { hasRequestedHighCare, setHasRequestedHighCare } = stepsContext.useHook();
  const { highcare } = useContent() || {};
  const { sendMultilpleCommandsWithOptimisticUpdate } = useCommands();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: params } = useParamsQuery();
  const { data: conditions } = useConditionsQuery();

  const handleCommand: SubmitHandler<HighCareFormType> = async ({ companyName, representative: { phone, email } }) => {
    setIsSubmitting(true);
    try {
      const commands: Commands[] = [
        { type: 'enterPhoneNumber', payload: { phoneNumber: phone } },
        { type: 'enterEmail', payload: { email } },
        { type: 'requestSupport', payload: {} },
      ];
      if (conditions?.companyNameEditable && companyName) {
        commands.unshift({ type: 'enterCompanyName', payload: { companyName } });
      }
      await sendMultilpleCommandsWithOptimisticUpdate({
        params: {
          representative: { ...(params?.representative as Params['representative']), phone, email },
          companyName,
        },
        commands,
      });
      setHasRequestedHighCare(true);
    } catch (_) {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (hasRequestedHighCare && isSubmitting) {
      showNextPage();
    }
  }, [hasRequestedHighCare, isSubmitting]);

  return (
    <HighCare
      content={<RichContentWithTooltip content={highcare} className={pageIntroContent} />}
      disableCompanyName={!conditions?.companyNameEditable}
      formData={params}
      translations={{
        inputLabels: {
          companyName: t('funnel:high_care_company_name_label'),
          phone: t('funnel:high_care_phone_label'),
          email: t('funnel:high_care_email_label'),
        },
        inputPlaceholders: {
          phonePlaceholder: t('funnel:high_care_phone_placeholder'),
        },
        validation: {
          required: t('validation:required'),
          phone: t('validation:phone_invalid'),
          email: t('validation:email_invalid'),
        },
        termsAndConditions: t('funnel:high_care_terms_and_conditions'),
        back: t('common:back'),
        forward: t('funnel:high_care_submit'),
      }}
      eventHandlers={{
        onBack: showPreviousPage,
        onSubmit: handleCommand,
      }}
      submitting={isSubmitting}
      defaultCountry='fr'
    />
  );
};
